'use client';

import { SigninResultCGType, TravelReviewCGType, TravelReviewCommentCGType } from '@/types/graphql/root';
import { useCallback, useEffect, useState } from 'react';
import { createTravelReviewComment, signinWithZzimToken } from '@/utils/actions';
import { useRouter } from 'next/navigation';
import { useSetAtom } from 'jotai';
import { blackTransparentLoaderModalAtom, centerToastModalAtom, confirmModalAtom } from '@/atom/modal';
import Refresh2020 from '@/svg/sharing-trips/refresh_20_20.svg';
import { filterNullUndefined } from '@/utils';
import SharingTripReportModal from './sharing-trip-report-modal';
import TripPostComment from './trip-post-comment';
import MessageLine3232 from '@svg/sharing-trips/message_line_32_32.svg';

export default function SharingTripPostCommentContainer({
  post,
  comments,
}: {
  post: TravelReviewCGType;
  comments: TravelReviewCommentCGType[] | null;
}) {
  const router = useRouter();

  const setConfirmModal = useSetAtom(confirmModalAtom);
  const setCenterToastModal = useSetAtom(centerToastModalAtom);
  const setBlackTransparentLoaderModal = useSetAtom(blackTransparentLoaderModalAtom);

  const [isLoggedin, setIsLoggedin] = useState(false);
  const [signinResult, setSigninResult] = useState<SigninResultCGType | null>(null);

  const [commentValue, setCommentValue] = useState('');

  const [lastRefreshedAt, setLastRefreshedAt] = useState(new Date().getTime());

  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [reportCommentId, setReportCommentId] = useState<number>(-1);

  useEffect(() => {
    signinWithZzimToken().then((signinResult) => {
      if (!signinResult?.accessToken) {
        setIsLoggedin(false);
      } else {
        setIsLoggedin(true);
        setSigninResult(signinResult ?? null);
      }
    });

    return () => {
      setConfirmModal({ visible: false });
    };
  }, []);

  const onClickSubmitComment = useCallback(() => {
    const createComment = async () => {
      try {
        setBlackTransparentLoaderModal(true);
        await createTravelReviewComment({
          travelReviewId: post.id,
          content: commentValue,
        });
        setCommentValue('');
        router.refresh();
      } catch (error) {
        setCenterToastModal({
          text: '댓글 등록에 실패했습니다.',
          renderingTime: 1000,
        });
      } finally {
        setBlackTransparentLoaderModal(false);
      }
    };

    if (commentValue === '') {
      setCenterToastModal({
        text: '댓글을 입력해주세요.',
        renderingTime: 1000,
      });
      return;
    }

    setConfirmModal({
      visible: true,
      title: '댓글 등록',
      message: '부적절한 댓글은 삭제될 수 있습니다.',
      onConfirm: () => {
        setConfirmModal({ visible: false });
        createComment();
      },
      onClose: () => {
        setConfirmModal({ visible: false });
      },
      onOuterClick: () => {
        setConfirmModal({ visible: false });
      },
    });
  }, [commentValue]);

  const onClickRefreshComment = useCallback(async () => {
    if (new Date().getTime() - lastRefreshedAt < 10000) {
      setCenterToastModal({
        text: '댓글 새로고침은\n10초에 한번만 가능합니다.',
        renderingTime: 1000,
      });
      return;
    }

    setLastRefreshedAt(new Date().getTime());
    setBlackTransparentLoaderModal(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    router.refresh();
    setBlackTransparentLoaderModal(false);
  }, [lastRefreshedAt]);

  const onClickReportComment = useCallback(async (commentId: number) => {
    const signinResult = await signinWithZzimToken();
    if (!signinResult) {
      setConfirmModal({
        visible: true,
        title: '로그인 필요',
        message: '로그인으로 이동합니다.',
        onClose: () => {
          setConfirmModal({ visible: false });
        },
        onConfirm: () => {
          setConfirmModal({ visible: false });
          window.location.href =
            process.env.NEXT_PUBLIC_ZZIMCAR_URL + 'user/signin?redirectUrl=' + window.location.href;
        },
      });
      return;
    }
    setReportCommentId(commentId);
    setReportModalVisible(true);
  }, []);

  const getCommentCount = useCallback((comments: TravelReviewCommentCGType[]) => {
    const parentCount = comments?.length ?? 0;
    const children = comments?.map((comment) => comment?.children).flat(Infinity);

    return parentCount + (children?.length ?? 0);
  }, []);

  return (
    <>
      <div className={`w-full mt-40pxr ${filterNullUndefined(comments).length > 0 && 'pb-60pxr'}`}>
        <div className='flex items-center justify-between px-20pxr py-12pxr border-y border-new-gray-200'>
          <div className='flex items-center gap-4pxr body-14-regular text-new-gray-900'>
            댓글 <span className='sub-title-14-bold'>{getCommentCount(comments ?? [])}</span>
          </div>

          <button
            className='w-20pxr h-20pxr shrink-0 flex items-center justify-center'
            onClick={() => {
              onClickRefreshComment();
            }}
          >
            <Refresh2020 width={20} height={20} />
          </button>
        </div>

        {filterNullUndefined(comments).map((comment) => (
          <TripPostComment
            key={`post_${post.id}_comment_${comment?.id}`}
            postId={post.id}
            comment={comment}
            onClickReportComment={onClickReportComment}
            isParent={true}
            isLoggedin={isLoggedin}
          />
        ))}
        {filterNullUndefined(comments).length === 0 && (
          <div className='w-full h-132pxr flex flex-col items-center justify-center'>
            <MessageLine3232 />
            <div className='body-14-regular text-new-gray-800 whitespace-pre-line text-center'>
              댓글이 없습니다.{'\n'}첫 댓글을 작성해 보세요.
            </div>
          </div>
        )}
      </div>

      <div
        className={`h-50pxr px-20pxr border-y border-new-gray-200 flex justify-between items-center gap-8pxr ${!isLoggedin && 'cursor-pointer'}`}
        onClick={(e) => {
          if (isLoggedin) return;
          setConfirmModal({
            visible: true,
            title: '로그인 필요',
            message: '로그인으로 이동합니다.',
            onClose: () => {
              setConfirmModal({ visible: false });
            },
            onConfirm: () => {
              setConfirmModal({ visible: false });
              window.location.href =
                process.env.NEXT_PUBLIC_ZZIMCAR_URL + 'user/signin?redirectUrl=' + window.location.href;
            },
          });
        }}
      >
        {isLoggedin && (
          <>
            <input
              type='text'
              placeholder={`불쾌감을 주는 욕설과 악플은 삭제될 수 있습니다.`}
              disabled={!isLoggedin}
              value={commentValue}
              onChange={(e) => {
                if (e.target.value.length > 1000) return;
                setCommentValue(e.target.value);
              }}
              className={`flex-1 outline-none body-14-regular text-new-gray-900 bg-white placeholder:text-new-gray-500`}
            />
            <button
              className={`shrink-0 w-50pxr h-32pxr flex items-center justify-center rounded-6pxr text-white body-14-md ${
                commentValue.length === 0 ? 'bg-new-gray-300' : 'bg-new-point-500'
              }`}
              disabled={commentValue.length === 0}
              onClick={() => onClickSubmitComment()}
            >
              등록
            </button>
          </>
        )}
        {!isLoggedin && (
          <>
            <div className={`flex-1 outline-none body-14-regular text-new-gray-500 bg-white`}>
              로그인 후 댓글을 작성할 수 있습니다.
            </div>
            <div className='shrink-0 w-50pxr h-32pxr flex items-center justify-center rounded-6pxr text-white body-14-md bg-new-gray-300'>
              등록
            </div>
          </>
        )}
      </div>

      <SharingTripReportModal
        visible={reportModalVisible}
        setVisible={setReportModalVisible}
        reviewId={post.id}
        commentId={reportCommentId}
      />
    </>
  );
}
